<script setup lang="ts">
import SavedLocation from "@/domains/locations/savedLocation.ts";
import { useSavedLocationStore } from "@/domains/locations/savedLocationStore.ts";
import { useDashboardNavStore } from "@/stores/dashboardNavStore.ts";
import { computed, ref } from "vue";
import CustomSolidButton from "../../ui-elements/buttons/custom-buttons/CustomSolidButton.vue";
import LocationDisplayCard from "./LocationDisplayCard.vue";
import LocationEditModal from "./LocationEditModal.vue";
import LocationRequestModal from "./LocationRequestModal.vue";

const searchQuery = ref("");
const showAddDialog = ref(false);
const editingLocation = ref<SavedLocation | null>(null);

const savedLocationsStore = useSavedLocationStore();

const unfilteredLocations = computed<SavedLocation[]>(
  () => savedLocationsStore.getSavedLocations,
);

const filteredLocations = computed(() => {
  if (searchQuery.value) {
    return unfilteredLocations.value.filter((location) => {
      // name match
      if (
        location.name
          ?.toLowerCase()
          .includes(searchQuery.value.toLowerCase().trim())
      )
        return true;
      // address match
      if (
        location.address
          ?.toLowerCase()
          .includes(searchQuery.value.toLowerCase().trim())
      )
        return true;
      // type match e.g. depot
      if (
        location.type
          .toLowerCase()
          .includes(searchQuery.value.toLowerCase().trim())
      )
        return true;
      return false;
    });
  }

  return unfilteredLocations.value;
});

const dashboardNavStore = useDashboardNavStore();

const navigateToLocationDetails = (location: SavedLocation) => {
  dashboardNavStore.showLocationDetails(location.localId);
};
</script>

<template>
  <v-container class="px-5">
    <!-- Search bar -->
    <v-card-text class="mb-5">
      <v-text-field
        v-model="searchQuery"
        clearable
        placeholder="Search saved charging stations & locations..."
        hide-details
      ></v-text-field>
    </v-card-text>
    <!-- Saved chargers section -->
    <v-card-title class="mb-3 d-flex align-center justify-space-between">
      Fleet saved locations
      <CustomSolidButton @click="showAddDialog = true"> Add location </CustomSolidButton>
    </v-card-title>
    <LocationRequestModal v-model:showDialog="showAddDialog" />
    <LocationEditModal
      :location="editingLocation"
      @close="editingLocation = null"
    />
    <v-card-text v-if="!filteredLocations.length && searchQuery">
      No results found for '{{ searchQuery }}'
    </v-card-text>
    <v-card-text v-else-if="!filteredLocations.length">
      No saved fleet locations
    </v-card-text>
    <v-card-text v-else>
      <v-row no-gutters>
        <v-col
          cols="12"
          md="6"
          v-for="(location, index) in filteredLocations"
          :key="'location-card-' + index"
          :class="index % 2 === 0 ? 'pr-0  pr-md-2' : 'pl-0  pl-md-2'"
          class="mb-3"
        >
          <LocationDisplayCard
            :location="location"
            @click="() => navigateToLocationDetails(location)"
            @edit="() => (editingLocation = location)"
          />
        </v-col>
      </v-row>
    </v-card-text>
  </v-container>
</template>
